/**
 * The file browser's CACHE: one folder tree, held in memory, plus the document
 * list of every place this tab has visited.
 *
 * Why it exists, in the user's words:
 *
 *   "why does it have to work so hard after every click. Can't we cache the
 *    folder structure in the browser for a quicker response? ... When the
 *    client changes directory, change directory and then read what's in it. If
 *    the cache needs to be updated then, do it, and then update the frontend
 *    seamlessly, but don't stall or hold or first read and then display."
 *
 * So the rule this module exists to keep is: **a click repaints from what we
 * already have, and the network happens afterwards, underneath.** Nothing on
 * this screen clears itself to wait for a response.
 *
 * Four things make that true, and each of them is a decision rather than an
 * optimisation:
 *
 * 1. **The tree is loaded ONCE.** `GET /v1/folders` returns every folder this
 *    person has, flat, with `parentId`, `depth` and `docCount`. It is bounded
 *    by the server -- 100 children per folder, depth 10 -- so it is tens of
 *    rows and it lives in memory for the life of the tab. The rail, the
 *    breadcrumb, the subfolder rows and the toolbar are all DERIVED from it,
 *    synchronously, so navigating cannot need a request to redraw them.
 *
 * 2. **Only the document list is fetched, and it never blanks the pane.** A
 *    view already visited repaints from `views[key].rows` on the same frame as
 *    the click; the request that follows replaces those rows when it answers.
 *    A view never visited has no rows to show and says so with a placeholder,
 *    not a spinner over an emptied pane -- and `prefetch()` on hover means the
 *    common case is that it has been fetched before it is clicked.
 *
 * 3. **Mutations are applied here first.** Create, rename, delete and move
 *    change the cache, then call the server, then reconcile. A refusal reverts
 *    to the snapshot taken before the change and rethrows, so the caller can
 *    put the server's own sentence on screen. None of this goes through
 *    `DocList`'s old `act()` helper, which reloaded both panes on success and
 *    would have overwritten the optimistic state mid-flight (TASK-042 §6d).
 *
 * 4. **The cache is a HINT, and it is always revalidated.** The persisted copy
 *    in `localStorage` is there for the first paint after a reload; the tree is
 *    refetched on every start, on window focus, when the tab becomes visible,
 *    after every mutation, and whenever a folder's children come back
 *    disagreeing with it. A stale entry therefore lives for one request, which
 *    is what "make a stale cache impossible to notice" means in practice.
 *
 * ### Invalidation: why this and not a per-user SSE channel
 *
 * The user asked for the server to notify the client. There is an SSE stream
 * per DOCUMENT (`/v1/events/:docId`) with a Redis fan-out, but **no per-user
 * channel**, and adding one is a new ticketed route, a ticket-minting route, a
 * `userChannel()` beside `docChannel()`, a publish at five call sites in
 * `service/folders.ts` and an `EventSource` here -- more than the half-day the
 * brief set as the threshold. So this ships the cheap revalidation, and the
 * SEAM is deliberately one function: `invalidate()`. Everything that could ever
 * learn "the tree changed" calls it and nothing else. A per-user channel is
 * then an `EventSource` whose `onmessage` calls `invalidate()`, and no other
 * line of this module moves.
 */

import { api } from './api'
import { myUserId, readStored, scopedKey, writeStored } from './me'
import { Reverted, optimistic } from './optimistic'
import type { DocRow, Folder, View } from './components/doclist-types'
import {
  viewQuery,
  withDocMoved,
  withFolderAdded,
  withFolderDeleted,
  withFolderMoved,
  withFolderRenamed,
} from './components/doclist-types'

/** Keyed by user id (see `lib/me.ts`): a tree names folders. */
const TREE_KEY = 'unomena.docs.tree'
/** Bumped when the stored shape changes, so an old entry is ignored, not read. */
const TREE_VERSION = 2
/** How long a cached document list is used without a background refetch. */
const DOC_TTL_MS = 15_000

export function viewKey(view: View): string {
  return view.kind === 'folder' ? `folder:${view.id}` : view.kind
}

/**
 * What the wire is asked for a view.
 *
 * A place asks the place -- `GET /v1/folders/:id/children` answers with its
 * direct contents, and the root answers to the literal `root`, which has no id
 * because it has no row. `?folder=unfiled` still names the predicate.
 */
function urlFor(view: View): string {
  if (view.kind === 'folder') return `/v1/folders/${encodeURIComponent(view.id)}/children`
  if (view.kind === 'root') return '/v1/folders/root/children'
  return `/v1/docs${viewQuery(view)}`
}

interface ChildrenResponse {
  docs?: DocRow[]
  folders?: Folder[]
}

export interface ViewEntry {
  rows: DocRow[]
  /** When these rows came back, for the "is it worth refetching" question. */
  at: number
  /** A request is in flight for this view. The rows stay on screen throughout. */
  pending: boolean
  /** The last refusal, if the rows on screen are older than a failed attempt. */
  error: string
}

/** Re-exported so the screen has one import for the whole optimistic path. */
export { Reverted }

let tempSeq = 0

class FolderStore {
  /** The whole tree, flat. Everything about a PLACE is derived from this. */
  folders = $state<Folder[]>([])
  /** True once anything has been painted -- from the cache or from the server. */
  hydrated = $state(false)
  /** A tree request is in flight. The rail keeps what it has while it is. */
  refreshing = $state(false)
  /** A tree-level failure, for the caller to put on screen. */
  error = $state('')

  /** Document lists, by view key. Never cleared on navigation. */
  views = $state<Record<string, ViewEntry>>({})

  #started = false
  #userId: string | null = null
  #treeInFlight: Promise<void> | null = null
  #docInFlight = new Map<string, Promise<void>>()

  /*
   * How many times each view's cache has been thrown away. BUG-024.
   *
   * Invalidating by deleting the entry is not enough on its own: the pointer
   * is resting on the destination of a drop, hover prefetches it, and the read
   * -- cheaper than the write that holds the document's lock -- comes back
   * BEFORE the move commits. Without a generation, that answer, which does not
   * contain the document, is stored with a fresh timestamp and trusted for
   * fifteen seconds: the person is told "moved to Project A", opens Project A,
   * and it is not there.
   *
   * So every fetch records the generation it started under and refuses to
   * store an answer for a world that has since changed.
   */
  #gen = new Map<string, number>()

  /** Throw away a view's cache, and make any answer already in flight stale. */
  #invalidate(key: string): void {
    delete this.views[key]
    this.#gen.set(key, (this.#gen.get(key) ?? 0) + 1)
    // A request already running answers for the world as it was, so a caller
    // arriving now must start its own rather than adopt that one.
    this.#docInFlight.delete(key)
  }

  /* ------------------------------------------------------------- the tree */

  /**
   * Called once from the screen. Starts the persisted read and the network
   * read TOGETHER -- the cached tree needs `user.id` to be keyed by a person,
   * and waiting for it before even asking the server would trade one stall for
   * another.
   *
   * The cache is only painted if the server has not already answered, so the
   * two cannot fight: whichever arrives first is what is on screen, and the
   * server's answer always wins in the end.
   */
  start(): void {
    if (this.#started) return
    this.#started = true
    void this.#hydrateFromCache()
    this.refresh().catch(() => {})
  }

  async #hydrateFromCache(): Promise<void> {
    const userId = await myUserId()
    this.#userId = userId
    if (!userId) return
    // The server got there first. Its answer is authoritative and a cached one
    // painted over it would be a step backwards on screen.
    if (this.hydrated) return
    const raw = readStored(scopedKey(TREE_KEY, userId))
    if (!raw) return
    try {
      const parsed = JSON.parse(raw)
      if (!parsed || parsed.v !== TREE_VERSION || !Array.isArray(parsed.folders)) return
      if (this.hydrated) return
      this.folders = parsed.folders as Folder[]
      this.hydrated = true
    } catch {
      /* a corrupt entry is a cache miss, not a failure */
    }
  }

  #persist(): void {
    if (!this.#userId) return
    // Optimistic rows are not persisted: a placeholder id that survived a
    // reload would be a folder in the rail that no request can ever resolve.
    const settled = this.folders.filter((f) => !f.pending)
    writeStored(
      scopedKey(TREE_KEY, this.#userId),
      JSON.stringify({ v: TREE_VERSION, at: Date.now(), folders: settled }),
    )
  }

  /**
   * Refetch the tree, without disturbing what is on screen until it answers.
   *
   * Coalesced: several callers during one flight share it, because focus,
   * navigation and a mutation's reconcile routinely land together.
   */
  refresh(): Promise<void> {
    if (this.#treeInFlight) return this.#treeInFlight
    this.refreshing = true
    const flight = (async () => {
      try {
        const res = await api<{ folders: Folder[] }>('/v1/folders')
        const next = res.folders ?? []
        // Optimistic rows the server has not seen yet are kept, or a create
        // would flicker out of the rail and back in as its POST lands.
        const pending = this.folders.filter((f) => f.pending)
        this.folders = pending.length ? [...next, ...pending] : next
        this.hydrated = true
        this.error = ''
        this.#persist()
      } catch (err) {
        // A rail that will not load must not take the list down with it, and a
        // rail that IS loaded must not be emptied by a failed refresh: the
        // cache stays exactly as it was and the caller decides what to say.
        this.error = describe(err)
        throw err
      } finally {
        this.refreshing = false
        this.#treeInFlight = null
      }
    })()
    this.#treeInFlight = flight
    flight.catch(() => {})
    return flight
  }

  /**
   * THE SEAM. Everything that learns the tree may have changed calls this.
   *
   * Today its callers are: window focus, the tab becoming visible, every
   * mutation's reconcile, and a folder's children coming back disagreeing with
   * the cache. A per-user SSE channel would be one more caller and would
   * change nothing else in this file.
   */
  invalidate(): void {
    this.refresh().catch(() => {})
  }

  /** Focus and visibility revalidation. Returns its own teardown. */
  watch(): () => void {
    const onFocus = () => this.invalidate()
    const onVisible = () => {
      if (document.visibilityState === 'visible') this.invalidate()
    }
    addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }

  /* --------------------------------------------------------- the documents */

  entry(view: View): ViewEntry | undefined {
    return this.views[viewKey(view)]
  }

  /**
   * Make sure this view's documents are on their way, WITHOUT clearing what is
   * on screen.
   *
   * Called from the effect that watches the URL. Rows already held are left
   * exactly where they are and are replaced only when the response lands, so a
   * navigation between two visited folders never shows a gap.
   */
  ensureDocs(view: View, opts: { force?: boolean } = {}): Promise<void> {
    const key = viewKey(view)
    const held = this.views[key]
    if (!opts.force && held && !held.pending && Date.now() - held.at < DOC_TTL_MS) {
      return Promise.resolve()
    }
    // Rejects, deliberately: the caller is the screen, and only the screen
    // knows that a folder answering `access` means "drop the dead `?folder=`"
    // rather than "say something went wrong".
    return this.#fetchDocs(view)
  }

  /**
   * Fetch a view's documents ahead of the click -- on hover, on focus.
   *
   * This is what makes the FIRST visit to a folder feel instant too. Pointing
   * at a row is a reliable signal of intent and costs one bounded request; by
   * the time the click lands the rows are usually already here, and when they
   * are not, nothing was lost.
   */
  prefetch(view: View): void {
    const key = viewKey(view)
    if (this.views[key] || this.#docInFlight.has(key)) return
    this.#fetchDocs(view).catch(() => {})
  }

  #fetchDocs(view: View): Promise<void> {
    const key = viewKey(view)
    const running = this.#docInFlight.get(key)
    if (running) return running

    // The world this answer will describe (BUG-024).
    const gen = this.#gen.get(key) ?? 0
    const stale = () => (this.#gen.get(key) ?? 0) !== gen

    const held = this.views[key]
    this.views[key] = {
      rows: held?.rows ?? [],
      at: held?.at ?? 0,
      pending: true,
      error: '',
    }

    const flight = (async () => {
      try {
        const res = await api<ChildrenResponse>(urlFor(view))
        if (stale()) {
          // Something changed this view while the request was out -- a move
          // into it, most likely. Storing this would cache the "before" as the
          // now. Drop the placeholder (a plain delete: the invalidation that
          // made us stale already bumped the generation) so the next reader
          // fetches afresh.
          delete this.views[key]
          return
        }
        this.views[key] = { rows: res.docs ?? [], at: Date.now(), pending: false, error: '' }
        // The children response carries this folder's subfolders, which is the
        // server telling us -- for free, on a request we were making anyway --
        // whether the tree we are drawing from is still the tree it has.
        if (view.kind === 'folder' || view.kind === 'root') {
          this.#reconcileChildren(view.kind === 'folder' ? view.id : null, res.folders ?? [])
        }
      } catch (err) {
        if (stale()) {
          delete this.views[key]
          throw err
        }
        const previous = this.views[key]
        this.views[key] = {
          rows: previous?.rows ?? [],
          at: previous?.at ?? 0,
          pending: false,
          error: describe(err),
        }
        throw err
      } finally {
        // Only if this flight is still the one that owns the slot.
        // `#invalidate` already cleared it, and a fresh request may have taken
        // it since; evicting that one would let a third start in parallel.
        if (!stale()) this.#docInFlight.delete(key)
      }
    })()

    this.#docInFlight.set(key, flight)
    // Marks the rejection handled, so a prefetch nobody awaited cannot become
    // an unhandled rejection; the returned promise still rejects for a caller
    // that wants to branch on the refusal.
    flight.catch(() => {})
    return flight
  }

  /**
   * Compare a folder's children against the cached tree, and revalidate if
   * they disagree.
   *
   * Deliberately a comparison and not a merge. Merging a partial answer into
   * the flat tree would have to decide what a MISSING row means -- deleted, or
   * simply not in this part of the answer -- and getting that wrong drops a
   * folder out of the rail while its documents are still in it. Asking for the
   * whole tree again is one bounded request and cannot be wrong.
   */
  #reconcileChildren(parentId: string | null, children: Folder[]): void {
    const mine = this.folders
      .filter((f) => f.parentId === parentId && !f.pending)
      .map((f) => `${f.id}:${f.name}:${f.docCount}`)
      .sort()
    const theirs = children.map((f) => `${f.id}:${f.name}:${f.docCount}`).sort()
    if (mine.length === theirs.length && mine.every((v, i) => v === theirs[i])) return
    this.invalidate()
  }

  /** Drop a document from every cached list. For delete and restore. */
  #removeDoc(docId: string): void {
    for (const [key, entry] of Object.entries(this.views)) {
      if (!entry.rows.some((d) => d.id === docId)) continue
      this.views[key] = { ...entry, rows: entry.rows.filter((d) => d.id !== docId) }
    }
  }

  /** Drop it from ONE list: the place it has just left. */
  #removeDocFrom(key: string, docId: string): void {
    const entry = this.views[key]
    if (!entry) return
    this.views[key] = { ...entry, rows: entry.rows.filter((d) => d.id !== docId) }
  }

  #patchDoc(docId: string, patch: Partial<DocRow>): void {
    for (const [key, entry] of Object.entries(this.views)) {
      if (!entry.rows.some((d) => d.id === docId)) continue
      this.views[key] = {
        ...entry,
        rows: entry.rows.map((d) => (d.id === docId ? { ...d, ...patch } : d)),
      }
    }
  }

  /* ---------------------------------------------------------- the mutations */

  /**
   * Apply, call, reconcile -- and on a refusal put back exactly what was there.
   *
   * The snapshot is the whole cache, which is tens of folder rows and a
   * handful of document lists: cheap to copy, and one revert path to test
   * rather than one per mutation (§6d: "the revert is one function taking one
   * pending record").
   */
  #optimistic<T>(apply: () => void, call: () => Promise<T>): Promise<T> {
    return optimistic(
      () => {
        // The whole cache: tens of folder rows and a handful of document
        // lists. Cheap to copy, and one revert path rather than one per
        // mutation. The reducers in `doclist-types.ts` never mutate, so these
        // two references ARE the previous state.
        const folders = this.folders
        const views = { ...this.views }
        return () => {
          this.folders = folders
          this.views = views
        }
      },
      apply,
      call,
      () => this.invalidate(),
    )
  }

  /**
   * A folder created optimistically carries a PLACEHOLDER id until the server
   * answers with a real one. It is drawn and counted; it is not a navigation
   * target, because following it would ask for a folder that does not exist.
   */
  async createFolder(name: string, parentId: string | null): Promise<Folder> {
    const tempId = `pending-${++tempSeq}`
    const parentDepth = parentId ? (this.folders.find((f) => f.id === parentId)?.depth ?? 0) : -1
    const optimistic: Folder = {
      id: tempId,
      name,
      parentId,
      depth: parentDepth + 1,
      docCount: 0,
      pending: true,
    }
    const created = await this.#optimistic(
      () => {
        this.folders = withFolderAdded(this.folders, optimistic)
      },
      () =>
        api<Folder>('/v1/folders', {
          method: 'POST',
          body: JSON.stringify({ name, parentId }),
        }),
    )
    // Swap the placeholder for the real row the moment it is known, rather than
    // waiting for the reconcile: the id is what makes the row clickable.
    this.folders = withFolderAdded(
      this.folders.filter((f) => f.id !== tempId),
      { ...optimistic, ...created, pending: false },
    )
    this.#persist()
    return created
  }

  async renameFolder(id: string, name: string): Promise<void> {
    await this.#optimistic(
      () => {
        this.folders = withFolderRenamed(this.folders, id, name)
        this.#persist()
      },
      () => api(`/v1/folders/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    )
  }

  async moveFolder(id: string, parentId: string | null): Promise<void> {
    await this.#optimistic(
      () => {
        this.folders = withFolderMoved(this.folders, id, parentId)
        this.#persist()
      },
      () => api(`/v1/folders/${id}`, { method: 'PATCH', body: JSON.stringify({ parentId }) }),
    )
  }

  /** Deleting a folder unfiles its documents and moves its subfolders up. */
  async deleteFolder(id: string): Promise<{ unfiled: number; reparented: number }> {
    const out = await this.#optimistic(
      () => {
        this.folders = withFolderDeleted(this.folders, id)
        this.#invalidate(`folder:${id}`)
        // Its documents went to the root and its subfolders moved up, so the
        // root's cached list is out of date by exactly those documents.
        this.#invalidate('root')
        this.#persist()
      },
      () => api<{ unfiled: number; reparented: number }>(`/v1/folders/${id}`, { method: 'DELETE' }),
    )
    return out
  }

  async renameDoc(doc: DocRow, name: string): Promise<void> {
    await this.#optimistic(
      () => this.#patchDoc(doc.id, { name }),
      () => api(`/v1/docs/${doc.id}`, { method: 'PATCH', body: JSON.stringify({ name }) }),
    )
  }

  /**
   * Filing a document: it leaves the list it is in, the two counts change, and
   * the destination's cached list is dropped so its next visit is honest about
   * where the row landed in it.
   */
  async moveDoc(doc: DocRow, folderId: string | null): Promise<void> {
    const from = doc.folderId ?? null
    const fromKey = from ? `folder:${from}` : 'root'
    const toKey = folderId ? `folder:${folderId}` : 'root'
    await this.#optimistic(
      () => {
        this.folders = withDocMoved(this.folders, from, folderId)
        // It leaves the PLACE it was in -- and only that one. "All documents"
        // and Trash are not places (§1b): the document is still in them, filed
        // somewhere new, and removing it there would be a row vanishing from a
        // list it never left.
        this.#removeDocFrom(fromKey, doc.id)
        // The destination is re-read on its next visit rather than guessed at:
        // the row's position in it is the server's `updated_at` ordering.
        this.#invalidate(toKey)
        this.#patchDoc(doc.id, { folderId })
        this.#persist()
      },
      () =>
        api(`/v1/docs/${doc.id}/folder`, {
          method: 'PUT',
          body: JSON.stringify({ folderId }),
        }),
    )
    /*
     * And again now the write has COMMITTED (BUG-024).
     *
     * The invalidation above runs optimistically, before the PUT is sent, so a
     * read that starts after it -- the hover prefetch of the folder the
     * pointer is resting on, every time -- is answered by a server that has
     * not applied the move yet, and that answer is not stale by the generation
     * it started under. Bumping again here makes it stale, and costs nothing:
     * no request, and the destination was going to be re-read on its next
     * visit anyway.
     */
    this.#invalidate(toKey)
  }

  async deleteDoc(doc: DocRow): Promise<void> {
    await this.#optimistic(
      () => {
        this.folders = withDocMoved(this.folders, doc.folderId ?? null, null)
        this.#removeDoc(doc.id)
        this.#invalidate('archived')
        this.#persist()
      },
      () => api(`/v1/docs/${doc.id}`, { method: 'DELETE' }),
    )
  }

  /**
   * Give up my own access to a document somebody shared with me.
   *
   * NOT optimistic, and it is the one mutation on this screen that is not.
   * The server's answer decides whether the row should leave the list at all:
   * a caller who holds BOTH a personal grant and the organisation-wide one
   * still sees the document afterwards (`stillVisible: 'org'`, TASK-038 review
   * finding F5), so removing the row first and putting it back on the answer
   * would be a flicker that tells a person the opposite of what happened.
   *
   * `sessionOnly`, like every sharing call: those routes refuse any request
   * carrying an `Authorization` header, and `api()` attaches a developer
   * bearer whenever localStorage holds one.
   */
  async leaveDoc(doc: DocRow): Promise<{ revoked: boolean; stillVisible: string | null }> {
    const out = await api<{ revoked: boolean; stillVisible: string | null }>(
      `/v1/docs/${doc.id}/shares/me`,
      { method: 'DELETE', sessionOnly: true },
    )
    if (out.stillVisible) {
      // It is still theirs to see, at whatever role the remaining grant gives,
      // so the honest thing is to ask again rather than to guess the new role.
      this.#invalidate(doc.folderId ? `folder:${doc.folderId}` : 'root')
      this.invalidate()
    } else {
      this.folders = withDocMoved(this.folders, doc.folderId ?? null, null)
      this.#removeDoc(doc.id)
      this.#persist()
    }
    return out
  }

  async restoreDoc(doc: DocRow): Promise<void> {
    await this.#optimistic(
      () => {
        this.#removeDoc(doc.id)
        this.#invalidate('all')
        this.#invalidate('root')
      },
      () => api(`/v1/docs/${doc.id}/restore`, { method: 'POST' }),
    )
  }
}

function describe(err: unknown): string {
  const e = err as any
  return String((e && (e.detail || e.message)) || 'that did not work')
}

export const folderStore = new FolderStore()
